/**
 * Reference tokens: the raw ramps, straight from the design-samples lab
 * (.context/design-samples/lab-missing-pieces.html §1). Nothing outside
 * theme/tokens should import these — components and overrides consume the
 * semantic layer (./semantic), so a rebrand or a mode only re-points names.
 */
export const sand = {
  0: '#FFFFFF',
  25: '#F2EFE5',
  50: '#F7F5F0',
  100: '#F1EEE7',
  200: '#E2DED2',
  300: '#C9C3B2',
  400: '#9C9584',
  // The tertiary ink. Darkened from the lab's #6E695C, which was 5.02:1 on `bg`
  // and left no room for anything to be painted behind it — the hover wash sits
  // there and pushed it under 4.5:1. This value is 5.53:1 plain, 4.74:1 hovered.
  500: '#68634F',
  700: '#5A564A',
  850: '#2C2921',
  900: '#24221C',
  925: '#232019',
  950: '#1B1915',
} as const

export const amber = {
  400: '#FFC905',
  500: '#F0A800',
  // The star outline: amber 500's hue at full saturation, darkened to 36%
  // lightness, so the mark's border belongs to the star instead of reading as a
  // separate ink line drawn around it. Serves both modes, and clears two floors
  // at once: 3.30:1 on white (3:1 for graphical objects) and 1.62:1 against the
  // amber 500 fill it outlines. Darker than this and a row of stars dominates
  // whatever it sits in; lighter and the outline dissolves into the fill and
  // takes the shape cue with it. Both floors are asserted in tokens.test.ts.
  600: '#B98400',
} as const

export const cream = {
  100: '#FFF9E5',
} as const

export const green = {
  100: '#E8F2E4',
  300: '#A8DBAB',
  600: '#256B2A',
  950: '#243122',
} as const

export const red = {
  100: '#FBEAE8',
  300: '#F1988E',
  600: '#B3261E',
} as const

export const navy = {
  600: '#1D3A6C',
} as const

export const ink = {
  900: '#2E2E2D',
} as const

/**
 * Chart hues. Separate from the ramps above on purpose: those are built for text
 * and surface tints and land either under 2.2:1 or over 5.5:1 on white, with
 * nothing in between. A chart mark needs exactly that middle band, so these are
 * authored for it — every step sits at OKLCH lightness 0.45–0.62, the window
 * where a mark clears 3:1 against both the light card (#FFFFFF) and the dark one
 * (sand.925), which is why most of them serve both modes unchanged.
 *
 * `vermilion` and `jade` are the two poles of the rating scale, held at matched
 * chroma (0.172) so neither reads as the louder sibling. Jade is a teal-shifted
 * green rather than a grass green: at hue 148 the two poles are 1.8 ΔE apart
 * under simulated red-green colour blindness — the same colour — and moving the
 * positive pole to hue 170 lifts that to 8.9 ΔE, past the 8 target.
 */
export const vermilion = {
  300: '#EF6452',
  500: '#CB4333',
  700: '#A01206',
} as const

export const jade = {
  300: '#00B18A',
  // 500 is a shade darker than the mark band strictly needs. The doughnut and the
  // stacked bands print a value on top of this fill, and at OKLCH 0.575 neither
  // text token reached 4.5:1 on it — 0.545 gives white 4.67 and, as a bonus,
  // widens the pole separation from 8.9 to 9.9 ΔE.
  500: '#008466',
  700: '#00654E',
} as const

/**
 * The neutral midpoint of the rating scale. One value for both modes: a diverging
 * scale's midpoint is its lightest mark either way, and at 1.90:1 on white it is
 * deliberately the quietest thing in the chart. That relies on the percentage
 * labels the cards already draw as the second channel.
 */
export const sandViz = {
  300: '#C5BBA7',
} as const

/** The data-mark blue. `mark` is also categorical slot 1 — one anchor, not two. */
export const dataBlue = {
  100: '#73ACFF',
  200: '#5492EC',
  400: '#3C79D1',
  600: '#2460B7',
  800: '#06489C',
  mark: '#2275E8',
} as const

/**
 * The ground a context series sits on — the response-volume bars behind an NPS
 * line, for instance. Deliberately a washed-out tint of the data blue rather
 * than a neutral: those bars are the *same* series' sample size, not unrelated
 * context, so they should read as the line's own background. Pushed well outside
 * the mark lightness band (0.86 light, 0.40 dark) so it can never be mistaken
 * for a second measure — a mid-band grey outweighed the metric it sat behind,
 * which is backwards, and left direct labels at 4.37:1, under the 4.5 they need.
 * Values here carry labels, which is the relief the sub-3:1 fill requires.
 */
export const dataGround = { light: '#BDD3F2', dark: '#394860' } as const

export const dataTeal = { 500: '#009D9A' } as const
export const dataMagenta = { 500: '#AF38A9' } as const
